import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FaSpinner, FaExclamationCircle } from "react-icons/fa";
import { dailyReportingApi } from "./dailyReportingApi";
import SectionCard from "./Shared/SectionCard";
import TeamReporting, { TeamSearchInput } from "./Shared/TeamReporting";
import DateQuickPicker from "./Shared/DateQuickPicker";
import Modal from "./Shared/Modal";
import EmployeeReportContent from "./Shared/EmployeeReportContent";
import { ACCENT } from "./Shared/reportingStyles";

const todayIsoDate = () => new Date().toISOString().slice(0, 10);

const formatLogDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

function DailyReportingDashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const employeeFromUrl = searchParams.get("employee");

  const [date, setDate] = useState(searchParams.get("date") || todayIsoDate());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(employeeFromUrl || null);
  const [teamSearch, setTeamSearch] = useState("");

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await dailyReportingApi.getDashboard({ date });
      if (res?.data?.success) setData(res.data.data);
      else setError(res?.data?.message || "Failed to load dashboard.");
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load dashboard.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  useEffect(() => {
    if (employeeFromUrl) setSelectedEmployeeId(employeeFromUrl);
  }, [employeeFromUrl]);

  const handleDateChange = (newDate) => {
    setDate(newDate);
    const params = new URLSearchParams(searchParams);
    params.set("date", newDate);
    if (selectedEmployeeId) params.set("employee", selectedEmployeeId);
    setSearchParams(params, { replace: true });
  };

  const openEmployeeModal = (userId) => {
    setSelectedEmployeeId(String(userId));
    const params = new URLSearchParams(searchParams);
    params.set("employee", String(userId));
    params.set("date", date);
    setSearchParams(params, { replace: true });
  };

  const closeEmployeeModal = () => {
    setSelectedEmployeeId(null);
    const params = new URLSearchParams(searchParams);
    params.delete("employee");
    setSearchParams(params, { replace: true });
  };

  const team = data?.team_activity || [];

  const selectedEmployee = team.find((e) => String(e.user_id) === String(selectedEmployeeId));

  return (
    <div className="tailwind-container min-h-0 bg-slate-50 px-4 py-5 sm:px-8">
      <div className="daily-report-page mx-auto w-full max-w-[1600px] space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-slate-200 bg-white px-4 py-4 shadow-sm">
          <div>
            <h1 className="text-lg font-bold text-slate-900">Daily reporting</h1>
            <p className="mt-0.5 text-xs text-slate-500">
              Team activity overview · {formatLogDate(date)}
            </p>
          </div>
          <DateQuickPicker date={date} onChange={handleDateChange} />
        </div>

        {loading ? (
          <div className="flex flex-col items-center gap-2 py-16 text-slate-400">
            <FaSpinner className="animate-spin" size={18} style={{ color: ACCENT }} />
            <p className="text-xs">Loading…</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center gap-2 py-16 text-rose-500">
            <FaExclamationCircle size={18} />
            <p className="text-xs">{error}</p>
          </div>
        ) : (
          <>
            <SectionCard
              title="Team reporting"
              subtitle={`${team.length} employee${team.length === 1 ? "" : "s"} with submitted reports`}
              action={<TeamSearchInput value={teamSearch} onChange={setTeamSearch} />}
              emphasis
            >
              <TeamReporting team={team} onView={openEmployeeModal} search={teamSearch} />
            </SectionCard>
          </>
        )}

        <Modal
          open={!!selectedEmployeeId}
          onClose={closeEmployeeModal}
          title={selectedEmployee?.name || "Employee report"}
          subtitle={formatLogDate(date)}
          width="max-w-6xl"
          footer={
            <button
              type="button"
              className="daily-report-modal-footer-btn"
              onClick={closeEmployeeModal}
            >
              Close
            </button>
          }
        >
          <EmployeeReportContent employeeId={selectedEmployeeId} date={date} />
        </Modal>
      </div>
    </div>
  );
}

export default DailyReportingDashboard;
